/** @format */

/* eslint require-await: "warn", @typescript-eslint/non-nullable-type-assertion-style: "warn", @typescript-eslint/no-unsafe-enum-comparison: "warn" */

/* eslint-disable max-lines */
import { SvgGradients } from '@mpb-com/feather/components/atoms/svg-gradients';
import {
    Drawer,
    DrawerProps,
} from '@mpb-com/feather/components/molecules/drawer';
import '@mpb-com/feather/style.css';
import { ThemeProvider } from '@mpb-com/feather/theme';
import {
    Hydrate,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { SSRConfig, TFunction, appWithTranslation, i18n } from 'next-i18next';
import NextApp, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { Router } from 'next/router';
import pRetry from 'p-retry';
import React, { ReactNode } from 'react';
import { createGlobalStyle } from 'styled-components';

import { normalizeIsoCode } from '@toucan/common/helpers/language';
import { ServerSideProps } from '@toucan/common/nextjs';
import { ANONYMOUS_USER } from '@toucan/common/services/feature-flags/constants';
import {
    getMarketBySlug,
    getMarketSlugFromPath,
    transformMarketToAvailableMarkets,
} from '@toucan/common/services/market/helpers';
import { getConfig } from '@toucan/common/services/nextjs/settings';
import { User } from '@toucan/common/services/user/types';

import { DataLayerEventType } from '@toucan/client/global/analytics';
import {
    AdditionalHeaderContextProps,
    ApplicationContext,
    ApplicationContextProps,
    Blackout,
    LegalLinksContextProps,
    Modal,
    ModalContextProps,
    ModalOptions,
    ModalSize,
    ModalVariant,
    PromoCode,
    ScrollLock,
    globalStyles,
    hasPathNameChanged,
    sendMetrics,
    startMetrics,
} from '@toucan/client/global/application';
import { centToDecimal } from '@toucan/client/global/currency';
import { FeatureFlagProvider } from '@toucan/client/global/feature-flag/context';
import {
    AvailableMarket,
    Market,
    MarketCode,
    MarketContext,
    MarketContextProps,
    defaultMarket,
} from '@toucan/client/global/market';
import { Model } from '@toucan/client/global/model';
import {
    getMaskedPath,
    shouldRobotsIndexRoute,
} from '@toucan/client/global/nextjs';
import '@toucan/client/global/polyfills';
import {
    ProductConditionValue,
    ProductWithId,
    SavedProduct,
} from '@toucan/client/global/product';
import { theme } from '@toucan/client/global/theme';
import { calculateTotal } from '@toucan/client/global/transaction';
import { trustpilot } from '@toucan/client/global/trustpilot';
import { UserContext, UserContextProps } from '@toucan/client/global/user';

import {
    AddToBasketProduct,
    Basket,
    BasketItem,
    GetBasketOptions,
    addToBasket,
    getBasket,
    removeFromBasket,
} from '@toucan/client/core/basket';
import { bloomreachSDK } from '@toucan/client/core/bloomreach';
import { getLegalPageNavigation } from '@toucan/client/core/contentful/requests';
import { getMarkets } from '@toucan/client/core/fixtures';
import { getModel } from '@toucan/client/core/model';
import { QuoteItemRepeaterValue } from '@toucan/client/core/quote';
import {
    addSavedModel,
    addSavedProduct,
    geolocateUser,
    getAnonymousToken,
    getSavedModels,
    getSavedProducts,
    getUser,
    removeSavedModel,
    removeSavedProduct,
} from '@toucan/client/core/user';

import {
    BasketContext,
    BasketContextProps,
} from '@toucan/client/shared/context/basket';
import {
    SellFormContext,
    SellFormContextProps,
    SellFormErrors,
} from '@toucan/client/shared/context/sell-form';
import {
    featureFlagToDataLayer,
    setCookies,
    trackEventsViaDataLayer,
    trackViaDataLayer,
} from '@toucan/client/shared/helpers/analytics';

import { getRiskifiedSessionId } from '@toucan/client/features/checkout/helpers/riskified';
import { Intercom } from '@toucan/client/features/intercom/components/intercom';
import headerLockSubject, {
    HeaderLock,
} from '@toucan/client/features/layout/components/header/header-lock-subject';
import { trackPageViewForRiskified } from '@toucan/client/features/riskified';
import { getCanonicalHref } from '@toucan/client/features/seo/helpers/canonical';

import { basketProductsToDataLayerItem } from '@toucan/client/views/basket/helpers';

const queryClient = new QueryClient();

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface AppExtraProps {
    err?: any;
}

type AppWithFixtures = {
    availableMarkets: AvailableMarket[];
    activeMarket: Market;
};

export type AppState = ApplicationContextProps &
    MarketContextProps &
    SSRConfig &
    ServerSideProps &
    AppWithFixtures;

type AppWithExtraProps = AppProps<ServerSideProps> & AppExtraProps;

const Global = createGlobalStyle`${globalStyles}`;

export const SCROLL_LOCK_CONTENT_ID = 'scroll-lock-content';

class App extends NextApp<AppWithExtraProps, AppState> {
    private metricTimer: null | ReturnType<typeof setTimeout> = null;

    private readonly defaultCloseLabel = i18n?.t('Close modal');

    private currentPath = '';

    public readonly state = {
        miniBasket: {
            visible: false,
        },
        blackout: {
            visible: false,
        },
        modal: {
            visible: false,
            content: null,
            size: ModalSize.SMALL,
            closeLabel: this.defaultCloseLabel,
            isClosable: true,
            variant: ModalVariant.DEFAULT,
        } as ModalContextProps,
        drawer: {
            open: false,
            children: null,
        } as DrawerProps,
        user: undefined,
        userInitialised: false,
        activeMarket: {} as Market,
        availableMarkets: [] as AvailableMarket[],
        geolocatedMarket: undefined,
        isLoggedIn: false,
        scrollLocks: [] as ScrollLock[],
        touchMoveSelectors: [] as string[],
        previousScrollPosition: null as number | null,
        basketModelSlugs: {},
        basket: {
            items: [],
        } as Basket,
        basketInitialised: false,
        isBasketLoading: true,
        sellForm: {
            sellValues: [] as QuoteItemRepeaterValue[],
            buyValues: [] as QuoteItemRepeaterValue[],
            formErrors: [] as SellFormErrors,
            scrollToBuyIndex: -1,
            hasInitialised: false,
        },
        additionalHeaderDisplayBoundary: {},
        isSavedProductsLoading: true,
        isSavedModelsLoading: true,
        savedProducts: [] as SavedProduct[],
        savedModels: [] as Model[],
        promoCode: undefined,
        stickyFooterHeight: 0,
        legalLinks: {},
    };

    private get availableMarkets(): AvailableMarket[] {
        if (this.state.availableMarkets.length > 0) {
            return this.state.availableMarkets;
        }

        return this.props.pageProps.availableMarkets;
    }

    private get activeMarket(): Market {
        if (this.state.activeMarket.iso) {
            return this.state.activeMarket;
        }

        return this.props.pageProps.activeMarket ?? defaultMarket;
    }

    /**
     * Fetch and save fixture data to application state.
     * This can be called to set fixture data when the page is not server rendered
     */
    private reloadFixtures = async () => {
        const { markets } = await getMarkets();
        const marketSlug = getMarketSlugFromPath(window.location.pathname);
        const activeMarket = getMarketBySlug(marketSlug, markets);
        const availableMarkets = transformMarketToAvailableMarkets(markets);

        this.setState({
            availableMarkets,
            activeMarket,
        });
    };

    /**
     * Get the user object from the server and populate the state used
     * by the user context.
     * Takes an optional callback function to be fired once the user has been fetched,
     * which can be used to track user data.
     */
    private getUser = async (
        callback?: (customerId?: User['customerId']) => void
    ): Promise<void> => {
        const { code } = this.activeMarket;
        return new Promise(async (resolve) => {
            try {
                const response = await getUser(code);
                const user = response.data;
                this.setState(
                    {
                        user,
                        isLoggedIn: true,
                        userInitialised: true,
                    },
                    resolve
                );

                callback?.(user.customerId);

                this.getSavedProducts();
                this.getSavedModels();

                const dataLayerEvents = [
                    {
                        event: DataLayerEventType.USER_LOADED,
                        userId: response.data.customerId,
                        loginStatus: true,
                    },
                    {
                        event: DataLayerEventType.LOGIN,
                        userId: response.data.customerId,
                    },
                ];

                trackEventsViaDataLayer(dataLayerEvents);
            } catch (error: any) {
                if (error?.response && error?.response.status === 401) {
                    const { featureFlags = [] } = this.props.pageProps;
                    const isAnonymousUserEnabled =
                        featureFlags.find(
                            (flag) => flag.name === ANONYMOUS_USER
                        )?.isEnabled ?? false;

                    if (!isAnonymousUserEnabled) {
                        // 401 means not logged in and no anonymous token yet — fetch one
                        await getAnonymousToken(code);
                    }
                }

                this.setState(
                    {
                        user: undefined,
                        isLoggedIn: false,
                        userInitialised: true,
                    },
                    resolve
                );

                // Fire the callback on failure so that we can signal that the
                // getUser request has finished.
                // We expect errors here for non-logged in users.
                callback?.();

                trackViaDataLayer({
                    event: DataLayerEventType.USER_LOADED,
                    userId: null,
                    loginStatus: false,
                });
            }
        });
    };

    private sendFeatureFlagDataLayerEvents = () => {
        const { featureFlags = [] } = this.props.pageProps;

        featureFlags.forEach((flag) => {
            if (!flag.isEnabled) {
                return;
            }

            const event = featureFlagToDataLayer(flag.name, flag.variant?.name);

            trackViaDataLayer(event);
        });
    };

    private readonly sendRiskifiedLoadedEvent = () => {
        const riskifiedSessionId = getRiskifiedSessionId({
            callRoute: 'App.sendRiskifiedLoadedEvent',
        });

        trackViaDataLayer({
            event: DataLayerEventType.RISKIFIED_SCRIPT_LOAD,
            MpbSessionId: riskifiedSessionId,
        });
    };

    private geolocateUser = async (): Promise<void> => {
        const response = await geolocateUser();

        const geolocatedMarket = response?.data?.userMarket as MarketCode;

        if (!geolocatedMarket) {
            return;
        }

        this.setState({ geolocatedMarket });
    };

    /**
     * Fetch the users saved products and store them in the state
     * @async
     * @returns {Promise<void>}
     */
    private getSavedProducts = async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const savedProducts = await getSavedProducts(
                    this.activeMarket,
                    this.availableMarkets,
                    i18n?.t as TFunction
                );
                this.setState(
                    { savedProducts, isSavedProductsLoading: false },
                    resolve
                );
            } catch (error: any) {
                console.error(error?.message);
                reject();
            }
        });
    };

    /**
     * Whether a product is saved or not.
     *
     * @param {number} id the product id
     * @returns {boolean} true if the product is saved to the user's favourites
     */
    private isSavedProduct = (id: number | undefined): boolean => {
        const { savedProducts } = this.state;
        return (
            !!id &&
            savedProducts.some((product: ProductWithId) => product.id === id)
        );
    };

    /**
     * Add a product to a users saved products and re-fetch the users saved
     * products from the api
     *
     * @param {number} id - The product id to add to the users saved products
     * @async
     * @returns {Promise<void>}
     */
    private addSavedProduct = async (id: number): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const newSavedProduct = await addSavedProduct(
                    this.activeMarket,
                    this.availableMarkets,
                    id,
                    i18n?.t as TFunction
                );

                if (!newSavedProduct) {
                    reject();
                    return;
                }

                // Check the new saved product doesn't already exist in our SavedProducts
                const savedProductExists = this.state.savedProducts.some(
                    (product) => product.id === newSavedProduct.id
                );

                if (!savedProductExists) {
                    this.setState(
                        {
                            savedProducts: [
                                newSavedProduct,
                                ...this.state.savedProducts,
                            ],
                        },
                        resolve
                    );
                }
            } catch (error: any) {
                console.log(error?.message);
                reject();
            }
        });
    };

    /**
     * Remove a product from a users saved products and re-fetch the users saved
     * products from the api
     *
     * @param {number} id - The product id to remove from the users saved products
     * @async
     * @returns {Promise<void>}
     */
    private removeSavedProduct = async (id: number): Promise<void> => {
        const { code } = this.activeMarket;

        return new Promise(async (resolve, reject) => {
            try {
                const { data } = await removeSavedProduct(code, id);

                // Remove the product from our savedProducts state
                if (data.status === 'OK') {
                    this.setState(
                        {
                            savedProducts: this.state.savedProducts.filter(
                                (products) => products.id !== id
                            ),
                        },
                        resolve
                    );
                }
            } catch (error: any) {
                console.log(error?.message);
                reject();
            }
        });
    };

    /**
     * Whether a model is saved or not.
     *
     * @param {number} id the model id
     * @returns {boolean} true if the model is saved to the user's favourites
     */
    private isSavedModel = (id: number | undefined): boolean => {
        const { savedModels } = this.state;
        return !!id && savedModels.some((model: Model) => model.id === id);
    };

    /**
     * Fetch the users saved models and store them in the state
     * @async
     * @returns {Promise<void>}
     */
    private getSavedModels = async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const savedModels = await getSavedModels(this.activeMarket);
                this.setState(
                    { savedModels, isSavedModelsLoading: false },
                    resolve
                );
            } catch (error: any) {
                console.error(error?.message);
                reject();
            }
        });
    };

    /**
     * Add a model to a users saved models and re-fetch the users saved
     * models from the api
     *
     * @param {number} id - The model id to add to the users saved models
     * @async
     * @returns {Promise<void>}
     */
    private addSavedModel = async (id: number): Promise<void> => {
        const { code } = this.activeMarket;

        return new Promise(async (resolve, reject) => {
            try {
                const {
                    data: { model: newSavedModel },
                } = await addSavedModel(code, id);

                // Check the new saved model doesn't already exist in our SavedModels
                const savedModelExists = this.state.savedModels.some(
                    (model) => model.id === newSavedModel?.id
                );

                if (!savedModelExists) {
                    this.setState(
                        {
                            savedModels: [
                                newSavedModel,
                                ...this.state.savedModels,
                            ],
                        },
                        resolve
                    );
                }
            } catch (error: any) {
                console.log(error?.message);
                reject();
            }
        });
    };

    /**
     * Remove a model from a user's saved models and re-fetch the user's saved
     * models from the api
     *
     * @param {number} id - The model id to remove from the users saved models
     * @async
     * @returns {Promise<void>}
     */
    private removeSavedModel = async (id: number): Promise<void> => {
        const { code } = this.activeMarket;

        return new Promise(async (resolve, reject) => {
            try {
                const { data } = await removeSavedModel(code, id);

                // Remove the model from our savedModels state
                if (data.status === 'OK') {
                    this.setState(
                        {
                            savedModels: this.state.savedModels.filter(
                                (models) => models.id !== id
                            ),
                        },
                        resolve
                    );
                }
            } catch (error: any) {
                console.log(error?.message);
                reject();
            }
        });
    };

    /**
     * Get a mapping of model ID to URL slug for each unique model in the basket
     *
     * TODO Remove this once KEZ-1627 is done and the model slug is added to the
     * basket response
     *
     * @param items {BasketItem[]} the items in the basket
     */
    private getBasketModelSlugs = async (items: BasketItem[]) => {
        const { code } = this.activeMarket;

        // get the unique set of models from the basket
        const uniqueModels = items.reduce(
            (uniqueSet: BasketItem[], basketItem: BasketItem) => {
                return uniqueSet.find(
                    (uniqueItem: BasketItem) =>
                        uniqueItem.modelId === basketItem.modelId
                )
                    ? uniqueSet
                    : [...uniqueSet, basketItem];
            },
            []
        );

        // for each of the unique models, get the model data
        const models: Model[] = await Promise.all(
            uniqueModels.map((item: BasketItem) => getModel(item.modelId, code))
        );

        // generate our mapping of model ID to slug
        return models.reduce((basketModelSlugs: any, model: Model) => {
            return {
                ...basketModelSlugs,
                [model.id]: model.urlSegment,
            };
        }, {});
    };

    /**
     * Get the user's current basket
     */
    private getBasket = async (options?: GetBasketOptions) => {
        const { code } = this.activeMarket;
        try {
            const basket = await pRetry(() => getBasket(code, options), {
                retries: 2,
                minTimeout: 2000,
            });

            const basketModelSlugs = await this.getBasketModelSlugs(
                basket.items
            );

            this.setState({
                basketModelSlugs,
                basket,
                isBasketLoading: false,
                basketInitialised: true,
            });

            return basket;
        } catch (error: any) {
            console.error(error?.message);
            return null;
        }
    };

    /**
     * Add a product to the basket.
     */
    private addToBasket = async (
        product: AddToBasketProduct,
        quantity: number
    ) => {
        const { code } = this.activeMarket;

        this.setState({
            isBasketLoading: true,
        });

        const { condition, modelId, id } = product;

        const isNewProduct = condition === ProductConditionValue.NEW;
        const productId = isNewProduct ? undefined : id;

        const basket = await addToBasket(modelId!, productId, quantity, code);

        const addToBasketDataLayerPayload = {
            event: DataLayerEventType.ADD_TO_BASKET,
            items: basketProductsToDataLayerItem(
                [
                    {
                        ...product,
                        id: productId,
                    },
                ],
                basket.currency
            ),
            // TODO the following properties will be removed once
            // the items updates have been made in GTM
            productId: isNewProduct ? null : product.id,
            productSku: isNewProduct ? null : product.sku,
            productImageUrl: product.image,
            modelId,
            modelName: product.name,
            condition: product.condition,
            productValue: centToDecimal(product.value ?? 0),
        };

        trackViaDataLayer(addToBasketDataLayerPayload);

        this.setState({
            basket,
            isBasketLoading: false,
        });
    };

    /**
     * Remove a product from the basket.
     */
    private removeFromBasket = async (item: BasketItem, quantity: number) => {
        const { code } = this.activeMarket;

        headerLockSubject.addLock(HeaderLock.BASKET_ITEM_ADDED, item.id);
        try {
            this.setState({
                isLoading: true,
            });

            const { condition, modelId, id } = item;
            const isNewProduct = condition === ProductConditionValue.NEW;
            const productId = isNewProduct ? undefined : id;

            const basket = await removeFromBasket(
                modelId,
                productId,
                quantity,
                code
            );

            const removeFromBasketDataLayerPayload = {
                event: 'REMOVE_FROM_BASKET',
                items: basketProductsToDataLayerItem(
                    [
                        {
                            ...item,
                            id: productId,
                            image: item.imageUrl,
                        },
                    ],
                    basket.currency
                ),
                itemsBuyingTotalValue: calculateTotal([item]),
            };

            trackViaDataLayer(removeFromBasketDataLayerPayload);

            this.setState({
                isLoading: false,
                basket,
            });
        } catch (error: any) {
            console.error(error?.message);
        }

        setTimeout(() => {
            headerLockSubject.removeLock(HeaderLock.BASKET_ITEM_ADDED, item.id);
        }, 2000);
    };

    private isBasketValid = () => {
        const { basket } = this.state;
        return basket?.items.every((item) => item.availability === 'Available');
    };

    /**
     * Remove items from the basket that are unavailable
     */
    private removeUnavailableBasketItems = async () => {
        const { basket } = this.state;
        const itemsToRemove: BasketItem[] = [];

        basket?.items.forEach((item) => {
            if (item.availability === 'Unavailable') {
                itemsToRemove.push(item);
            }
        });

        return await Promise.all(
            itemsToRemove.map((item: BasketItem) =>
                this.removeFromBasket(item, 1)
            )
        );
    };

    private getNavigation = async (): Promise<void> => {
        const { code, iso } = this.activeMarket;

        const legalLinks = await getLegalPageNavigation(
            code,
            normalizeIsoCode(iso)
        );

        this.setLegalLinks(legalLinks);
    };

    private setBlackoutVisible = (isVisible: boolean) => {
        this.setState((prevState: ApplicationContextProps) => ({
            blackout: {
                ...prevState.blackout,
                visible: isVisible,
            },
        }));
    };

    private setModalVisible = (isVisible: boolean, options?: ModalOptions) => {
        const toggleScrollLock = () => {
            if (isVisible) {
                this.addPageScrollLock(ScrollLock.MODAL);
            } else {
                this.removePageScrollLock(ScrollLock.MODAL);
            }
        };

        this.setState((prevState: ApplicationContextProps) => {
            const prevModal = prevState.modal;

            return {
                modal: {
                    ...prevModal,
                    size: options?.size ?? prevModal.size,
                    visible: isVisible,
                    isClosable: options?.isClosable ?? true,
                    // Reset to the default close label when the modal is no longer visible
                    closeLabel: isVisible
                        ? options?.closeLabel ?? prevState.modal.closeLabel
                        : this.defaultCloseLabel,
                    onClose: options?.onClose,
                    variant: options?.variant ?? ModalVariant.DEFAULT,
                },
                blackout: {
                    ...prevState.blackout,
                    visible: isVisible,
                },
            };
        }, toggleScrollLock);
    };

    private setModalCloseLabel = (closeLabel: string) => {
        this.setState((prevState: ApplicationContextProps) => ({
            modal: {
                ...prevState.modal,
                closeLabel,
            },
        }));
    };

    private setModalContent = (content: ReactNode) => {
        this.setState((prevState: ApplicationContextProps) => ({
            modal: {
                ...prevState.modal,
                content,
            },
        }));
    };

    private setModalSize = (size: ModalSize) => {
        this.setState((prevState: ApplicationContextProps) => ({
            modal: {
                ...prevState.modal,
                size,
            },
        }));
    };

    private openDrawer = (drawer: Omit<DrawerProps, 'open'>) => {
        this.setState({ drawer: { ...drawer, open: true } }); // Only set specified props when the drawer is opened to prevent previous drawer configurations leaking into the current one
    };

    private closeDrawer = () => {
        this.setState((prevState: ApplicationContextProps) => ({
            drawer: { ...prevState.drawer, open: false }, // Preserve existing props on close so that the drawer closes with the same configuration
        }));
    };

    private setMiniBasketVisible = (isVisible: boolean) => {
        this.setState((prevState: BasketContextProps) => ({
            miniBasket: {
                ...prevState.miniBasket,
                visible: isVisible,
            },
        }));
    };

    private setLegalLinks = (
        legalLinks: LegalLinksContextProps | undefined
    ) => {
        this.setState((prevState: AppContext) => ({
            ...prevState,
            legalLinks,
        }));
    };

    private setAdditionalHeaderDisplayBoundary = ({
        start,
        end,
    }: AdditionalHeaderContextProps) => {
        this.setState({
            additionalHeaderDisplayBoundary: { start, end },
        });
    };

    private registerRouterEvents = () => {
        Router.events.on('routeChangeComplete', this.routeChangeComplete);
        Router.events.on('routeChangeStart', () => {
            // explicity check the path that we've got set in state.
            // sometimes the router event is called with the same path as the current path (back/forward browser navigation).
            if (!hasPathNameChanged(this.currentPath)) {
                return;
            }

            this.setModalVisible(false);
        });
    };

    private routeChangeComplete = () => {
        const { router } = this.props;
        const newPath = getMaskedPath(router);

        // Don't send page view if only query params have changed
        if (newPath !== this.currentPath) {
            this.sendPageView();
            trackPageViewForRiskified(router.route);
        }
        this.currentPath = newPath;
    };

    private sendPageView = () => {
        const { router } = this.props;

        trackViaDataLayer({
            event: 'PAGE_VIEW',
            pageTemplate: router?.route?.replace(/^\//, ''),
        });
    };

    /**
     * Stops the page from being able to be scrolled
     */
    private lockPageScroll = () => {
        const element =
            document.getElementById(SCROLL_LOCK_CONTENT_ID) ?? document.body;
        disableBodyScroll(element, {
            allowTouchMove: (el: Element) => {
                return this.state.touchMoveSelectors.some((selector) =>
                    el.matches(selector)
                );
            },
        });
    };

    /**
     * Allows the page to be scrolled
     */
    private unlockPageScroll = () => {
        const element =
            document.getElementById(SCROLL_LOCK_CONTENT_ID) ?? document.body;
        enableBodyScroll(element);
    };

    /**
     * Registers an area that is locking the scroll
     */
    private addPageScrollLock = (
        lock: ScrollLock,
        allowTouchMoveSelectors: string[] = []
    ) => {
        const locks = [...this.state.scrollLocks];

        if (!locks.includes(lock)) {
            locks.push(lock);

            if (locks.length === 1) {
                this.lockPageScroll();
            }
        }

        this.setState({
            scrollLocks: locks,
            touchMoveSelectors: [
                ...this.state.touchMoveSelectors,
                ...allowTouchMoveSelectors,
            ],
        });
    };

    /**
     * Removes an area that is locking the scroll
     */
    private removePageScrollLock = (
        lock: ScrollLock,
        allowTouchMoveSelectors: string[] = []
    ) => {
        const locks = [...this.state.scrollLocks].filter((oldLock) => {
            return oldLock !== lock;
        });

        if (locks.length === 0) {
            this.unlockPageScroll();
        }

        this.setState({
            scrollLocks: locks,
            touchMoveSelectors: this.state.touchMoveSelectors.filter(
                (selector) => {
                    return !allowTouchMoveSelectors.includes(selector);
                }
            ),
        });
    };

    /**
     * Set the sell values to the sellForm state
     */
    private setSellValues = (
        sellValues: QuoteItemRepeaterValue[],
        callback = () => {}
    ) => {
        const quoteData = {
            sellValues,
            buyValues: this.state.sellForm.buyValues,
        };

        try {
            localStorage.setItem(
                this.sellFormLocalStorageKey,
                JSON.stringify(quoteData)
            );
        } catch (error) {
            console.error(error);
        }

        this.setState(
            (prevState: SellFormContextProps) => ({
                sellForm: {
                    ...prevState.sellForm,
                    sellValues,
                },
            }),
            callback
        );
    };

    private setPromoCode = (promoCode?: PromoCode) => {
        return this.setState({
            promoCode,
        });
    };

    /**
     * Set the buy values to the sellForm state
     */
    private setBuyValues = (
        buyValues: QuoteItemRepeaterValue[],
        callback = () => {}
    ) => {
        const quoteData = {
            sellValues: this.state.sellForm.sellValues,
            buyValues,
        };

        try {
            localStorage.setItem(
                this.sellFormLocalStorageKey,
                JSON.stringify(quoteData)
            );
        } catch (error) {
            console.error(error);
        }

        this.setState(
            (prevState: SellFormContextProps) => ({
                sellForm: {
                    ...prevState.sellForm,
                    buyValues,
                },
            }),
            callback
        );
    };

    /**
     * Set the index of the buy value that we want to scroll to
     */
    private setScrollToBuyIndex = (index: number) => {
        this.setState((prevState: SellFormContextProps) => ({
            sellForm: {
                ...prevState.sellForm,
                scrollToBuyIndex: index,
            },
        }));
    };

    /**
     * Set the sell form errors
     */
    private setSellFormErrors = (formErrors: SellFormErrors) => {
        this.setState((prevState: SellFormContextProps) => ({
            sellForm: {
                ...prevState.sellForm,
                formErrors,
            },
        }));
    };

    private onSellFormDataInitialised = () => {
        this.setState((prevState: SellFormContextProps) => ({
            sellForm: {
                ...prevState.sellForm,
                hasInitialised: true,
            },
        }));
    };

    /**
     * Get the local storage key used to store the sell form data
     */
    private get sellFormLocalStorageKey() {
        const { code } = this.activeMarket;

        return `${code}__SELL_FORM`;
    }

    /**
     * Get the quote data from local storage
     */
    private getQuoteData = () => {
        try {
            const quoteData = JSON.parse(
                localStorage.getItem(this.sellFormLocalStorageKey)!
            );

            const hasSellItems = Boolean(quoteData?.sellValues?.[0]?.data);
            const sellValues = hasSellItems
                ? quoteData?.sellValues
                : [{ id: 0 }];

            this.setBuyValues(quoteData?.buyValues ?? [], () => {
                this.setSellValues(sellValues, this.onSellFormDataInitialised);
            });
        } catch (error) {
            this.onSellFormDataInitialised();
            console.error(error);
        }
    };

    /**
     * Clear the sell form data
     */
    private clearSellFormData = () => {
        try {
            this.setBuyValues([]);
            this.setSellValues([
                {
                    id: 0,
                },
            ]);
        } catch (error) {
            console.error(error);
        }
    };

    get skuBlacklist(): string[] {
        return this.state.sellForm.buyValues.reduce(
            (accumulator: string[], buyItem: QuoteItemRepeaterValue) => {
                const sku = buyItem?.data?.product?.sku;
                if (sku) {
                    accumulator.push(sku);
                }
                return accumulator;
            },
            [] as string[]
        );
    }

    /**
     * Add class to body to signify that the mouse is being used
     */
    private static usingMouse() {
        document.body.classList.add('using-mouse');
    }

    /**
     * Remove class from body to signify that the keyboard is being used
     */
    private static usingKeyboard() {
        document.body.classList.remove('using-mouse');
    }

    private setStickyFooterHeight = (stickyFooterHeight: number): void => {
        this.setState({
            stickyFooterHeight,
        });
    };

    public componentDidMount(): void {
        const { publicRuntimeConfig } = getConfig();
        const { router } = this.props;

        this.metricTimer = publicRuntimeConfig.dev
            ? null
            : startMetrics(this.availableMarkets);
        this.geolocateUser();
        this.getUser(bloomreachSDK.initialise);
        this.getBasket();
        this.getNavigation();
        this.registerRouterEvents();
        this.routeChangeComplete();
        this.getQuoteData();
        this.sendFeatureFlagDataLayerEvents();

        setCookies(router);

        document.body.addEventListener('mousedown', App.usingMouse);
        document.body.addEventListener('keydown', App.usingKeyboard);
        window.addEventListener('beforeunload', sendMetrics);
        trustpilot.attach();

        window.addEventListener('RiskifiedScriptLoaded', () => {
            this.sendRiskifiedLoadedEvent();
        });
        window.addEventListener('popstate', this.closeDrawer);
    }

    public componentWillUnmount(): void {
        document.body.removeEventListener('mousedown', App.usingMouse);
        document.body.removeEventListener('keydown', App.usingKeyboard);
        window.removeEventListener('beforeunload', sendMetrics);
        window.removeEventListener(
            'RiskifiedScriptLoaded',
            this.sendRiskifiedLoadedEvent
        );
        window.removeEventListener('popstate', this.closeDrawer);
        clearTimeout(this.metricTimer!);
    }

    public componentDidUpdate(
        prevProps: AppWithExtraProps,
        prevState: AppState
    ) {
        if (prevState?.activeMarket.code !== this.state.activeMarket.code) {
            this.getNavigation();
        }
    }

    public render() {
        const { Component, pageProps, err, router } = this.props;
        const { url } = pageProps;
        const {
            modal,
            drawer,
            blackout,
            miniBasket,
            user,
            isLoggedIn,
            basketModelSlugs,
            basket,
            isBasketLoading,
            sellForm,
            additionalHeaderDisplayBoundary,
            savedProducts,
            savedModels,
            isSavedProductsLoading,
            isSavedModelsLoading,
            promoCode,
            stickyFooterHeight,
            legalLinks,
        } = this.state;

        const appContextStore: ApplicationContextProps = {
            modal,
            drawer,
            blackout,
            additionalHeaderDisplayBoundary,
            legalLinks,
            stickyFooterHeight,
            setStickyFooterHeight: this.setStickyFooterHeight,
            setBlackoutVisible: this.setBlackoutVisible,
            setModalVisible: this.setModalVisible,
            setModalCloseLabel: this.setModalCloseLabel,
            setModalContent: this.setModalContent,
            setModalSize: this.setModalSize,
            openDrawer: this.openDrawer,
            closeDrawer: this.closeDrawer,
            addPageScrollLock: this.addPageScrollLock,
            removePageScrollLock: this.removePageScrollLock,
            userAgentDetectedAsBot: pageProps?.userAgentDetectedAsBot ?? false,
            setAdditionalHeaderDisplayBoundary:
                this.setAdditionalHeaderDisplayBoundary,
        };

        const marketContextStore: MarketContextProps = {
            activeMarket: this.activeMarket,
            availableMarkets: this.availableMarkets,
            reloadFixtures: this.reloadFixtures,
        };

        const userContextStore: UserContextProps = {
            user,
            isLoggedIn,
            isSavedProductsLoading,
            isSavedModelsLoading,
            savedProducts,
            savedModels,
            geolocatedMarket: this.state.geolocatedMarket,
            isInitialised: this.state.userInitialised,
            getUser: this.getUser,
            getSavedProducts: this.getSavedProducts,
            isSavedProduct: this.isSavedProduct,
            addSavedProduct: this.addSavedProduct,
            removeSavedProduct: this.removeSavedProduct,
            isSavedModel: this.isSavedModel,
            getSavedModels: this.getSavedModels,
            addSavedModel: this.addSavedModel,
            removeSavedModel: this.removeSavedModel,
        };

        const basketContextStore: BasketContextProps = {
            basketModelSlugs,
            basket,
            miniBasket,
            setMiniBasketVisible: this.setMiniBasketVisible,
            isLoading: isBasketLoading,
            addToBasket: this.addToBasket,
            removeFromBasket: this.removeFromBasket,
            getBasket: this.getBasket,
            isInitialised: this.state.basketInitialised,
            isBasketValid: this.isBasketValid,
            removeUnavailableBasketItems: this.removeUnavailableBasketItems,
        };

        const sellFormContextStore: SellFormContextProps = {
            sellForm,
            promoCode,
            setPromoCode: this.setPromoCode,
            setScrollToBuyIndex: this.setScrollToBuyIndex,
            setSellValues: this.setSellValues,
            setBuyValues: this.setBuyValues,
            setFormErrors: this.setSellFormErrors,
            clearSellFormData: this.clearSellFormData,
            skuBlacklist: this.skuBlacklist,
        };

        const { publicRuntimeConfig } = getConfig();
        const { hostUri } = publicRuntimeConfig;

        const isSearchEngineIndexingDisabled =
            publicRuntimeConfig?.searchEngineIndexingDisabledMarkets?.includes(
                this.activeMarket.code
            );

        const shouldAddNoIndexMeta =
            isSearchEngineIndexingDisabled ||
            !shouldRobotsIndexRoute(router.route);

        return (
            <FeatureFlagProvider featureFlags={pageProps?.featureFlags}>
                <QueryClientProvider client={queryClient}>
                    <Hydrate state={pageProps?.dehydratedState}>
                        <ThemeProvider theme={theme}>
                            <ApplicationContext.Provider
                                value={appContextStore}
                            >
                                <MarketContext.Provider
                                    value={marketContextStore}
                                >
                                    <UserContext.Provider
                                        value={userContextStore}
                                    >
                                        <BasketContext.Provider
                                            value={basketContextStore}
                                        >
                                            <SellFormContext.Provider
                                                value={sellFormContextStore}
                                            >
                                                <React.Fragment>
                                                    <Head>
                                                        {shouldAddNoIndexMeta && (
                                                            <meta
                                                                name="robots"
                                                                content="noindex"
                                                            />
                                                        )}
                                                        <meta
                                                            httpEquiv="content-language"
                                                            content={normalizeIsoCode(
                                                                this
                                                                    .activeMarket
                                                                    .iso
                                                            )}
                                                        />
                                                        <link
                                                            rel="canonical"
                                                            href={getCanonicalHref(
                                                                hostUri,
                                                                url
                                                            )}
                                                            key="canonical"
                                                        />
                                                    </Head>
                                                    <Global />
                                                    <Component
                                                        {...pageProps}
                                                        err={err}
                                                    />
                                                    <Modal
                                                        className="modal"
                                                        closeLabel={
                                                            modal.closeLabel
                                                        }
                                                        size={modal.size}
                                                        isOpen={modal.visible}
                                                        isClosable={
                                                            modal.isClosable
                                                        }
                                                        onClose={() =>
                                                            modal.onClose
                                                                ? modal.onClose()
                                                                : this.setModalVisible(
                                                                      false
                                                                  )
                                                        }
                                                        variant={modal.variant}
                                                    >
                                                        {modal.content}
                                                    </Modal>
                                                    <Drawer
                                                        {...drawer}
                                                        onClose={() =>
                                                            drawer.onClose
                                                                ? drawer.onClose()
                                                                : this.closeDrawer()
                                                        }
                                                    />
                                                    <Intercom />
                                                    <Blackout
                                                        className="blackout"
                                                        isOpen={
                                                            blackout.visible
                                                        }
                                                        data-testid="site-blackout"
                                                    />
                                                    <SvgGradients />
                                                </React.Fragment>
                                            </SellFormContext.Provider>
                                        </BasketContext.Provider>
                                    </UserContext.Provider>
                                </MarketContext.Provider>
                            </ApplicationContext.Provider>
                        </ThemeProvider>
                    </Hydrate>
                </QueryClientProvider>
            </FeatureFlagProvider>
        );
    }
}

export default appWithTranslation(App);
